import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  MapPin, Calendar, Info, Camera, Compass, ChevronLeft, ChevronRight,
  CheckCircle, ArrowRight
} from 'lucide-react';
import { destinations } from '../utils/mockData';

export const DestinationDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [destination, setDestination] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    // Scroll to top when destination changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    const foundDest = destinations.find(d => d.slug === slug);
    if (foundDest) {
      setDestination(foundDest);
      document.title = foundDest.metaTitle;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', foundDest.metaDescription);
      }
    } else {
      navigate('/');
    }
  }, [slug, navigate]);

  if (!destination) return null;

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % destination.gallery.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + destination.gallery.length) % destination.gallery.length);
  };

  const relatedDestinations = destinations.filter(d => d.id !== destination.id).slice(0, 3);

  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      <Header onRequestItinerary={() => navigate('/tours')} />

      {/* Hero Section */}
      <section className="relative h-[70vh] md:h-[80vh] overflow-hidden">
        <img
          src={destination.heroImage}
          alt={`${destination.name} - India`}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 w-full">
            <div className="max-w-3xl">
              <div className="flex items-center space-x-2 mb-4">
                <MapPin className="h-6 w-6 text-[#C4B5A0]" />
                <span className="text-white text-lg">Destination Guide</span>
              </div>
              
              <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mb-4">
                {destination.name}
              </h1>
              
              <p className="text-xl text-white/90 italic mb-8">
                {destination.tagline}
              </p>

              <Link to="/tours">
                <Button className="bg-[#C4B5A0] hover:bg-[#6B4E37] text-white px-8 py-6 text-lg">
                  View Tours to {destination.name}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-12">
            {/* Overview */}
            <section>
              <h2 className="font-serif text-3xl text-[#2C2C2C] mb-6">Overview</h2>
              <p className="text-lg text-[#8B7D6B] leading-relaxed">
                {destination.overview}
              </p>
            </section>

            {/* Best Time to Visit */}
            <section className="bg-[#F5F3F0] rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Calendar className="h-6 w-6 text-[#C4B5A0]" />
                <h3 className="font-serif text-2xl text-[#2C2C2C]">Best Time to Visit</h3>
              </div>
              <p className="text-[#8B7D6B]">{destination.bestTime}</p>
            </section>

            {/* Top Attractions */}
            <section>
              <h2 className="font-serif text-3xl text-[#2C2C2C] mb-6">Top Attractions</h2>
              <div className="grid gap-4">
                {destination.attractions.map((attraction, index) => (
                  <div key={index} className="flex items-start space-x-3 bg-white p-4 rounded-lg shadow-sm">
                    <CheckCircle className="h-5 w-5 text-[#C4B5A0] flex-shrink-0 mt-1" />
                    <span className="text-[#2C2C2C]">{attraction}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Things to Do */}
            <section>
              <div className="flex items-center space-x-3 mb-6">
                <Compass className="h-6 w-6 text-[#C4B5A0]" />
                <h2 className="font-serif text-3xl text-[#2C2C2C]">Things to Do</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {destination.thingsToDo.map((activity, index) => (
                  <div key={index} className="flex items-start space-x-3 bg-[#F5F3F0] p-4 rounded-lg">
                    <ArrowRight className="h-5 w-5 text-[#6B4E37] flex-shrink-0 mt-1" />
                    <span className="text-[#2C2C2C]">{activity}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Photo Gallery */}
            <section>
              <div className="flex items-center space-x-3 mb-6">
                <Camera className="h-6 w-6 text-[#C4B5A0]" />
                <h2 className="font-serif text-3xl text-[#2C2C2C]">Photo Gallery</h2>
              </div>
              <div className="relative">
                <img
                  src={destination.gallery[currentImageIndex]}
                  alt={`${destination.name} ${currentImageIndex + 1}`}
                  className="w-full h-[400px] object-cover rounded-lg"
                />
                
                {/* Gallery Navigation */}
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-colors"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="h-6 w-6 text-[#2C2C2C]" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-colors"
                  aria-label="Next image"
                >
                  <ChevronRight className="h-6 w-6 text-[#2C2C2C]" />
                </button>

                {/* Thumbnails */}
                <div className="flex space-x-2 mt-4 overflow-x-auto">
                  {destination.gallery.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 ${
                        index === currentImageIndex ? 'border-[#C4B5A0]' : 'border-transparent'
                      }`}
                    >
                      <img src={img} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            </section>

            {/* How to Reach */}
            <section className="bg-white rounded-lg p-6 shadow-md">
              <div className="flex items-center space-x-3 mb-4">
                <Info className="h-6 w-6 text-[#C4B5A0]" />
                <h3 className="font-serif text-2xl text-[#2C2C2C]">How to Reach</h3>
              </div>
              <p className="text-[#8B7D6B]">{destination.howToReach}</p>
            </section>
          </div>

          {/* Right Column - Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-8">
              {/* Interesting Facts */}
              <div className="bg-[#6B4E37] text-white rounded-lg p-6 shadow-lg">
                <h3 className="font-serif text-2xl mb-4">Fascinating Facts</h3>
                <ul className="space-y-3">
                  {destination.facts.map((fact, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <span className="text-[#C4B5A0] font-bold mt-1">•</span>
                      <span className="text-sm">{fact}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA Card */}
              <div className="bg-white rounded-lg p-6 shadow-lg border border-[#F5F3F0]">
                <h3 className="font-serif text-2xl text-[#2C2C2C] mb-4">Plan Your Visit</h3>
                <p className="text-[#8B7D6B] mb-6">
                  Explore {destination.name} with our carefully curated tours and expert guides.
                </p>
                <Link to="/tours">
                  <Button className="w-full bg-[#C4B5A0] hover:bg-[#6B4E37] text-white mb-3">
                    Browse All Tours
                  </Button>
                </Link>
                <a
                  href="https://wa.me/917827884419?text=Hi!%20I'm%20interested%20in%20visiting%20${destination.name}.%20Please%20share%20tour%20details."
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white">
                    Chat on WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Related Destinations */}
        {relatedDestinations.length > 0 && (
          <section className="mt-16 pt-16 border-t border-[#F5F3F0]">
            <h2 className="font-serif text-3xl text-[#2C2C2C] mb-8 text-center">More Destinations</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedDestinations.map((dest) => (
                <Link
                  key={dest.id}
                  to={`/destinations/${dest.slug}`}
                  className="group block"
                >
                  <div className="relative overflow-hidden rounded-lg mb-4 h-64">
                    <img
                      src={dest.image}
                      alt={dest.name}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="font-serif text-2xl text-white mb-1">{dest.name}</h3>
                      <p className="text-white/90 text-sm italic">{dest.description}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      <Footer />
    </div>
  );
};
